import React, { useState, useEffect } from "react";
import { Row, Col, Spinner } from "reactstrap";
import CountUp from "react-countup";
import FeatherIcon from "feather-icons-react";

const card = {
  backgroundColor: "#ffffff",
  borderRadius:    12,
  border:          "1px solid #e2e8f0",
  boxShadow:       "0 1px 4px rgba(0,0,0,0.06)",
  padding:         "20px 22px",
  transition:      "box-shadow .2s, transform .2s",
};

const AgentControlMetrics = ({ onAgentsLoaded }) => {
  const [summary,      setSummary]      = useState({ total: 0, active: 0, disconnected: 0, never: 0 });
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const creds     = () => ({ apiUrl: localStorage.getItem("flaskApiUrl"), apiKey: localStorage.getItem("flaskApiKey") });
  const authFetch = async (path) => {
    const { apiUrl, apiKey } = creds();
    if (!apiUrl || !apiKey) throw new Error("Credentials not configured");
    const r = await fetch(`${apiUrl}${path}`, {
      headers: { "Content-Type": "application/json", "X-API-Key": apiKey },
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await authFetch("/api/wazuh/agents");
        if (data?.success && data.agents) {
          const a = data.agents;
          const s = {
            total:        a.length,
            active:       a.filter(x => x.status === "active").length,
            disconnected: a.filter(x => x.status === "disconnected").length,
            never:        a.filter(x => x.status === "never_connected").length,
          };
          setSummary(s);
          if (onAgentsLoaded) onAgentsLoaded(a);
        }
        try {
          const ev = await authFetch("/api/agents/status-events-recent?limit=8");
          if (ev?.success) setRecentEvents(ev.events || []);
        } catch { /* optional */ }
      } catch (e) { setError(e.message); }
      finally     { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const fmtAgo = (ts) => {
    if (!ts) return "—";
    const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (diff < 1)    return "just now";
    if (diff < 60)   return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return new Date(ts).toLocaleDateString();
  };

  const tiles = [
    { label: "Total Endpoints", value: summary.total,        icon: "layers",       accent: "#d87844" },
    { label: "Active",          value: summary.active,       icon: "check-circle", accent: "#16a34a" },
    { label: "Disconnected",    value: summary.disconnected,  icon: "wifi-off",     accent: "#dc2626" },
    { label: "Never Connected", value: summary.never,        icon: "slash",        accent: "#94a3b8" },
  ];

  if (loading) return (
    <Row className="g-3 mb-3">
      {[1,2,3,4].map(k => (
        <Col xl={3} md={6} key={k}>
          <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "center", height: 110 }}>
            <Spinner size="sm" style={{ color: "#d87844" }} />
          </div>
        </Col>
      ))}
    </Row>
  );

  return (
    <>
      <Row className="g-3 mb-3">
        {tiles.map((t, i) => (
          <Col xl={3} md={6} key={i}>
            <div
              style={card}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,.09)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = ""; }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#94a3b8", marginBottom: 6 }}>
                    {t.label}
                  </p>
                  <h2 style={{ fontSize: 34, fontWeight: 800, color: "#1e293b", marginBottom: 4, lineHeight: 1 }}>
                    {error
                      ? <span style={{ fontSize: 20, color: "#cbd5e1" }}>—</span>
                      : <CountUp start={0} end={t.value} duration={1.5} separator="," />
                    }
                  </h2>
                  {t.label === "Active" && summary.total > 0 && (
                    <p style={{ fontSize: 12, color: "#94a3b8", margin: 0 }}>
                      {Math.round((summary.active / summary.total) * 100)}% online
                    </p>
                  )}
                </div>
                <div style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: `${t.accent}16`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FeatherIcon icon={t.icon} size={20} style={{ color: t.accent }} />
                </div>
              </div>

              {t.label === "Active" && summary.total > 0 && (
                <div style={{ marginTop: 14, height: 4, backgroundColor: "#f1f5f9", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.round((summary.active / summary.total) * 100)}%`, backgroundColor: "#16a34a", borderRadius: 2, transition: "width 1s ease" }} />
                </div>
              )}
            </div>
          </Col>
        ))}
      </Row>

      {recentEvents.length > 0 && (
        <div style={{ ...card, marginBottom: 12, padding: "14px 22px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#94a3b8", marginBottom: 10 }}>
            Recent Status Changes
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {recentEvents.map((ev, i) => (
              <span key={i} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "4px 12px",
                backgroundColor: ev.event_type === "online" ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${ev.event_type === "online" ? "#bbf7d0" : "#fecaca"}`,
                borderRadius: 20, fontSize: 12,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, backgroundColor: ev.event_type === "online" ? "#16a34a" : "#dc2626" }} />
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{ev.agent_name}</span>
                <span style={{ color: "#94a3b8" }}>
                  {ev.event_type === "online" ? "came online" : "went offline"} · {fmtAgo(ev.timestamp)}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: "10px 16px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, color: "#dc2626", marginBottom: 12 }}>
          <FeatherIcon icon="alert-circle" size={14} className="me-2" />{error}
        </div>
      )}
    </>
  );
};

export default AgentControlMetrics;